import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtNumber } from '@/shared/lib/format'
import { ChartFrame, RankedBars, ScatterQuadrant, OutcomeStrip } from '@/shared/ui/charts'
import { Donut } from '@/shared/charts/Donut'
import { AskPanel, CouncilBoard, type Finding } from '@/shared/ui/council'
import { getGraph, listCoordination, type Coordination, type NarrativeGraph } from './api'
import { advise, ask } from './analytics/council'

/**
 * War Room analytics — the window, measured.
 *
 * ## What this is, next to the console
 *
 * The console at `/war-room` draws the window as one map and is the right surface for
 * "what is the shape of this". It is untouched by this screen and must stay so. What it
 * cannot do is answer a question with a number: how much reach sits on platforms we cannot
 * reply on, whether the fast threads are the dangerous ones, how much of the window nothing
 * has read. Those are the readings here, and they come from the same `GET /war-room/graph`
 * payload the console draws, so the two can never disagree about a total.
 *
 * ## There is no geography in this feature, and the page says so
 *
 * `warroom_thread` carries a `unit_path`, so a map of where a narrative is loudest is a
 * thing the schema anticipates — but it is null on every row, the thread DTO does not carry
 * it, and no geography unit in this deployment has a centroid. Drawing threads on a map of
 * Kenya would therefore mean inventing the one thing a map is for.
 *
 * The coordinates this page DOES use are the ones the feed actually measures: **velocity
 * against threat**, with engagements as area. That is a real coordinate space and it answers
 * the question a war room has at four in the afternoon — not "where is this" but "what is
 * dangerous AND moving", which a ranked list by either axis alone will always get wrong.
 *
 * ## Everything is windowed, and the window is a control
 *
 * `windowHours` is the single most consequential setting on the page: a subject that looks
 * quiet over six hours can be the whole story over seventy-two. It is in the URL, so a board
 * somebody is looking at can be sent to somebody else and mean the same thing.
 */
// Out to ten years — two electoral cycles — matching the console and the server clamp.
// Twenty-four hours stays the DEFAULT; it is the span a war room works in. Thirty days was a ceiling that silently answered "how did this build over the season?" with one
// month of it, and "what did they say last cycle?" with nothing at all.
const WINDOWS = [6, 12, 24, 48, 72, 168, 336, 720, 2160, 4320, 8760, 17520, 43800, 87600]

/** "3650 days" is not a decade to anybody. Days hold to 90 so the familiar spans keep their names. */
function spanLabel(h: number) {
  if (h <= 24) return `${h} hours`
  const d = Math.round(h / 24)
  if (d <= 90) return `${d} days`
  if (d < 365) return `${Math.round(d / 30)} months`
  const y = Math.round(d / 365)
  return y === 1 ? '1 year' : `${y} years`
}

type Tab = 'pressure' | 'narratives' | 'reach' | 'council'

const TABS: { id: Tab; label: string; hint: string }[] = [
  { id: 'pressure',   label: 'Pressure',   hint: 'Threat against velocity — what is dangerous and moving' },
  { id: 'narratives', label: 'Narratives', hint: 'The arguments, ranked by how fast they travel' },
  { id: 'reach',      label: 'Reach',      hint: 'Where the audience is, and where we can answer' },
  { id: 'council',    label: 'AI Council', hint: 'The window read as decisions' },
]

export function WarRoomAnalyticsPage() {
  const canRead = useCan('warroom:read')
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const windowHours = Number(params.get('window') ?? 24)
  const subjectId = params.get('subject')
  const tab = (TABS.find((t) => t.id === params.get('tab'))?.id ?? 'pressure') as Tab

  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const graph = useLoad<NarrativeGraph | null>(
    () => getGraph({ windowHours, subjectId: subjectId ?? undefined, limit: 400 }),
    [windowHours, subjectId])
  // The stand-down state matters, so the whole list is read and filtered here rather than
  // asking the server for live ones only — a signal somebody stood down is a decision, and
  // the count of them is worth seeing next to the count still open.
  const coordination = useLoad<Coordination[]>(
    () => listCoordination({ size: 100 }).then((p) => p.content), [])

  const g = graph.data
  const signals = coordination.data ?? []
  const findings = useMemo(
    () => (g ? advise(g, signals) : []), [g, signals])

  if (!canRead) return <Forbidden what="the War Room" />

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Analytics"
        description="The same window the console draws, measured. Every figure is scoped to the window and the subject selected here."
      />
      <ErrorStrip message={graph.error ?? coordination.error} />

      {graph.loading && !g ? <LoadingBlock label="Reading the window…" /> : !g ? null : (
        <>
          <Panel dense bodyClassName="p-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-erp-sm">
                <span className="text-erp-xs uppercase tracking-wide text-muted-foreground">
                  Window
                </span>
                <select value={windowHours}
                        onChange={(e) => set('window', e.target.value)}
                        className="rounded-md border border-border bg-background px-2 py-1.5 text-erp-sm">
                  {WINDOWS.map((h) => (
                    <option key={h} value={h}>
                      {spanLabel(h)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex items-center gap-2 text-erp-sm">
                <span className="text-erp-xs uppercase tracking-wide text-muted-foreground">
                  Subject
                </span>
                <select value={subjectId ?? ''}
                        onChange={(e) => set('subject', e.target.value || null)}
                        className="rounded-md border border-border bg-background px-2 py-1.5 text-erp-sm">
                  <option value="">Everyone we watch</option>
                  {g.subjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.name ?? 'Unnamed subject'}</option>
                  ))}
                </select>
              </label>

              <span className="text-erp-xs text-muted-foreground">
                Generated {new Date(g.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {' · '}response threshold {g.deployThreshold}/10
                {/* A sampled board that does not say so is a board that lies about its totals. */}
                {g.truncated && ` · showing the top ${g.threads.length} of ${fmtNumber(g.threadTotal)} threads by rank`}
              </span>
            </div>
          </Panel>

          <Headline g={g} signals={signals} />

          <div className="flex flex-wrap gap-1 border-b border-border" role="tablist">
            {TABS.map((t) => (
              <button key={t.id} role="tab" aria-selected={tab === t.id} title={t.hint}
                      onClick={() => set('tab', t.id === 'pressure' ? null : t.id)}
                      className={cn(
                        'px-3 py-2 text-erp-sm rounded-t-md border-b-2 -mb-px transition-colors',
                        tab === t.id
                          ? 'border-primary text-foreground font-medium'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50')}>
                {t.label}
                {t.id === 'council' && findings.some((f) => f.severity === 'critical') && (
                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-erp-xs px-1.5 min-w-[18px] h-[18px] tabular-nums">
                    {findings.filter((f) => f.severity === 'critical').length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {tab === 'pressure' && <PressureTab g={g} onOpen={(id) => navigate(`/war-room/threads/${id}`)} />}
          {tab === 'narratives' && <NarrativesTab g={g} signals={signals} />}
          {tab === 'reach' && <ReachTab g={g} />}
          {tab === 'council' && (
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <CouncilBoard
                  findings={findings}
                  title="What the window is saying"
                  description={`${findings.length} ${findings.length === 1 ? 'finding' : 'findings'} over the last ${spanLabel(windowHours)}, ranked by the engagements each is worth.`}
                  stakeUnit="engagement"
                  emptyMessage="Nothing in this window crosses a threshold. Widen it if you are checking whether something has been building."
                />
              </div>
              <AskPanel
                storageKey={`warroom-council-history:${subjectId ?? 'all'}`}
                description="Plain language. This window's figures go with the question, so the answer is about what is actually moving."
                placeholder="What should we answer first?"
                scopeNote={`Scoped to the last ${spanLabel(windowHours)}${subjectId ? ' and one subject' : ''}.`}
                context={() => windowContext(g, signals, findings, windowHours)}
                suggestions={[
                  'What should we answer first?', 'Is anything coordinated?',
                  'What is spreading fastest?', 'How much have we not read?',
                  'Where can we not reply?',
                ]}
                onAsk={(question) => ask(question, findings)}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * The window, written out for the model.
 *
 * Two things are stated as instructions rather than left to inference, because getting either
 * wrong in a war room is not a small error: unscored threads are not low-threat ones, and a
 * repeated phrase is evidence about strings and never about who is repeating them. A model
 * given the raw counts alone will cheerfully draw both conclusions.
 */
function windowContext(
  g: NarrativeGraph, signals: Coordination[], findings: Finding[], hours: number,
): string {
  const t = g.totals
  const live = signals.filter((s) => !s.stoodDownAt)
  const hot = g.threads
    .filter((x) => x.threat != null && x.threat >= g.deployThreshold)
    .sort((a, b) => (b.velocity ?? 0) - (a.velocity ?? 0))
    .slice(0, 10)

  return [
    'You are advising a Kenyan campaign\'s social media war room. Answer in plain language a',
    'communications lead can act on. Two rules you must not break:',
    '(1) An UNSCORED thread is one nobody has read. It is NOT a low-threat thread, and you may',
    'not treat the scored ones as representative of the window.',
    '(2) A repeated phrase is evidence that wording is circulating. It is NOT evidence of who is',
    'circulating it. Never name or imply an actor, group or campaign behind it.',
    'Do not draft any post — drafting happens elsewhere and every post needs a named human.',
    '',
    `WINDOW: last ${spanLabel(hours)} (${hours} hours), generated ${new Date(g.generatedAt).toLocaleString()}.`
      + ` Response threshold ${g.deployThreshold}/10.`
      + (g.truncated ? ` Showing the top ${g.threads.length} of ${g.threadTotal} threads by rank.` : ''),
    `TOTALS: ${t.threads} threads, ${t.narratives} narratives, ${t.engagements} engagements,`
      + ` ${t.aboveThreshold} above threshold, ${t.unscored} NEVER READ,`
      + ` ${t.draftsAwaiting} drafts awaiting a human, ${t.deployedToday} deployed today.`,
    '',
    'PLATFORMS (label, threads, engagements, can we reply):',
    ...g.platforms.filter((p) => p.threads > 0).map((p) => `- ${p.label}: ${p.threads} threads,`
      + ` ${p.engagements} engagements, ${p.velocity.toFixed(1)}/min,`
      + ` ${p.canPost ? 'can reply' : 'WATCH ONLY — no reply API exists'}`),
    '',
    'SUBJECTS (name, threads, engagements, share of threads read as hostile):',
    ...g.subjects.map((s) => `- ${s.name ?? 'unnamed'}: ${s.threads} threads,`
      + ` ${s.engagements} engagements, ${Math.round(s.hostileShare * 100)}% hostile,`
      + ` mean threat ${s.threatMean?.toFixed(1) ?? 'unread'}`),
    '',
    'NARRATIVES THAT HAVE BEEN READ (label, threads, engagements, velocity, worst threat, kind):',
    ...g.narratives.filter((n) => n.threatMax != null)
      .sort((a, b) => b.velocity - a.velocity).slice(0, 10)
      .map((n) => `- "${n.label}" about ${n.subjectName ?? 'unknown'}: ${n.threads} threads,`
        + ` ${n.engagements} engagements, ${n.velocity.toFixed(1)}/min, worst ${n.threatMax}/10, ${n.kind}`),
    '',
    hot.length > 0
      ? 'ABOVE THRESHOLD (platform, threat, velocity, engagements, drafts):\n'
        + hot.map((x) => `- ${x.platformCode} "${(x.title ?? '').slice(0, 70)}": threat ${x.threat}/10,`
          + ` ${x.velocity?.toFixed(1) ?? '—'}/min, ${x.engagements ?? 0} engagements,`
          + ` ${x.draftCount} drafts${x.draftCount === 0 && x.deployedCount === 0 ? ' — NOTHING DRAFTED' : ''}`).join('\n')
      : 'ABOVE THRESHOLD: nothing.',
    '',
    live.length > 0
      ? 'REPEATED PHRASING (the quoted string, distinct authors, threads, confidence out of 100):\n'
        + live.slice(0, 6).map((c) => `- "${c.phrase}": ${c.distinctAuthors} authors,`
          + ` ${c.threadCount} threads, ${c.windowMinutes} min, confidence ${c.confidence}`).join('\n')
      : 'REPEATED PHRASING: the scan matched none in this window.',
    '',
    'WHAT OUR OWN RULES ALREADY FLAGGED (agree, refine or disagree):',
    ...findings.map((f) => `- [${f.severity}] ${f.headline} ${f.evidence}`),
  ].join('\n')
}

// ─── headline ────────────────────────────────────────────────────────────────

function Headline({ g, signals }: { g: NarrativeGraph; signals: Coordination[] }) {
  const t = g.totals
  const live = signals.filter((s) => !s.stoodDownAt).length
  const unanswered = g.threads.filter(
    (x) => x.threat != null && x.threat >= g.deployThreshold
      && x.draftCount === 0 && x.deployedCount === 0).length

  return (
    <KpiStrip>
      <MetricCard label="Threads in window" value={fmtNumber(t.threads)}
        sub={`${fmtNumber(t.narratives)} distinct arguments`} />
      <MetricCard label="Above the threshold" value={fmtNumber(t.aboveThreshold)}
        sub={`response threshold ${g.deployThreshold}/10`} accent={t.aboveThreshold > 0} />
      {/* Unread is NOT low threat, and the sub-line has to say so — a tile reading "0 above
          threshold" beside 300 unread threads is a measurement of nothing. */}
      <MetricCard label="Nobody has read" value={fmtNumber(t.unscored)}
        sub="no rating yet — not the same as harmless" accent={t.unscored > 0} />
      <MetricCard label="Engagements" value={fmtNumber(t.engagements)}
        sub="the audience actually reached" />
      <MetricCard label="Unanswered and hot" value={fmtNumber(unanswered)}
        sub="serious enough to answer, nothing written" accent={unanswered > 0} />
      <MetricCard label="Repeated phrasing" value={fmtNumber(live)}
        sub={`${signals.length - live} stood down`} />
    </KpiStrip>
  )
}

// ─── tab: pressure ───────────────────────────────────────────────────────────

function PressureTab({ g, onOpen }: { g: NarrativeGraph; onOpen: (id: string) => void }) {
  const scored = g.threads.filter((x) => x.threat != null)
  const unscored = g.threads.length - scored.length
  /*
     The velocity axis is logarithmic, and that is a correction rather than a preference.

     Engagement rates are long-tailed: one thread at 6.7/min against a field sitting under
     0.1/min pins every other point to the left edge, and the plane degenerates into the
     ranked list it exists to replace. `log1p` spreads the body of the distribution without
     dropping the outlier or clipping the axis — the fast thread is still furthest right, it
     just no longer defines the whole scale. The axis label says it is a log scale, because a
     reader who assumes linear will misjudge every distance on it.
  */
  const maxVelocity = Math.max(...scored.map((x) => x.velocity ?? 0), 0)
  const vx = (v: number) => (maxVelocity <= 0
    ? 0 : Math.round((Math.log1p(v) / Math.log1p(maxVelocity)) * 100))

  const bands = new Map<string, number>()
  for (const x of g.threads) {
    const k = x.threat == null ? 'UNREAD'
      : x.threat >= g.deployThreshold + 2 ? 'CRITICAL'
      : x.threat >= g.deployThreshold ? 'HIGH'
      : x.threat >= Math.max(g.deployThreshold - 2, 1) ? 'MEDIUM' : 'LOW'
    bands.set(k, (bands.get(k) ?? 0) + 1)
  }
  const BAND_ORDER = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL', 'UNREAD'] as const
  const BAND_LABEL: Record<string, string> = {
    LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High', CRITICAL: 'Critical', UNREAD: 'Never read',
  }

  return (
    <div className="space-y-4">
      <Panel
        title="Threat against velocity"
        description="The only two coordinates this feed actually measures. Top-right is the emergency; bubble area is engagements."
      >
        <ScatterQuadrant
          xLabel="How fast it is moving (engagements/min, log scale)"
          yLabel="How dangerous it is (threat, 1–10)"
          height={360}
          onSelect={onOpen}
          points={scored.map((x) => ({
            id: x.id,
            label: x.title?.slice(0, 40) ?? x.platformCode,
            x: vx(x.velocity ?? 0),
            y: Math.round(((x.threat ?? 0) / 10) * 100),
            size: x.engagements ?? 0,
            tone: (x.threat ?? 0) >= g.deployThreshold
              ? (x.draftCount === 0 && x.deployedCount === 0 ? 'danger' : 'warn')
              : 'muted',
          }))}
        />
        <p className="mt-3 text-erp-xs text-muted-foreground">
          Red is serious enough to answer with nothing written yet; amber is serious enough
          and somebody has a draft in hand. A ranked list by threat alone puts a dangerous thread nobody is reading at the
          top, and a list by velocity alone puts a viral irrelevance there — which is why this
          is a plane and not a table.
          {unscored > 0 && ` ${fmtNumber(unscored)} threads carry no score and cannot be placed; they are counted in the ring on the right.`}
        </p>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Every thread, by threat band"
               description="An ordered scale on the ordinal ramp — 'never read' is a category, not a zero.">
          <Donut
            palette="ordinal"
            slices={BAND_ORDER.map((k) => ({
              id: k, label: BAND_LABEL[k]!, value: bands.get(k) ?? 0,
            }))}
            total={g.threads.length}
            centreLabel="Threads"
            formatValue={(v) => `${Math.round(v)}`}
            emptyMessage="No threads in this window."
          />
        </Panel>

        <Panel title="Fastest threads above the threshold"
               description="Velocity is engagements per minute. This is the order to work them in.">
          <RankedBars
            points={scored
              .filter((x) => (x.threat ?? 0) >= g.deployThreshold)
              .sort((a, b) => (b.velocity ?? 0) - (a.velocity ?? 0))
              .slice(0, 10)
              .map((x) => ({
                label: (x.title ?? x.authorHandle ?? x.platformCode).slice(0, 42),
                value: Number((x.velocity ?? 0).toFixed(1)),
                hint: `${x.platformCode} · threat ${x.threat}/10 · ${fmtNumber(x.engagements ?? 0)} engagements`
                  + (x.draftCount === 0 && x.deployedCount === 0 ? ' · nothing drafted' : ` · ${x.draftCount} drafts`),
              }))}
            formatValue={(n) => `${n}/min`}
            emptyMessage="Nothing in this window is serious enough to need an answer."
          />
        </Panel>
      </div>
    </div>
  )
}

// ─── tab: narratives ─────────────────────────────────────────────────────────

function NarrativesTab({ g, signals }: { g: NarrativeGraph; signals: Coordination[] }) {
  const coordinated = g.narratives.filter((n) => n.kind === 'COORDINATED')
  const live = signals.filter((s) => !s.stoodDownAt)

  // A narrative the council has not read carries an empty sentiment. Keyed as-is it renders
  // a legend row with a count and no name, which reads as a rendering bug rather than as the
  // absence it is — and it is the largest slice, so the chart's biggest wedge is nameless.
  const sentiment = new Map<string, number>()
  for (const n of g.narratives) {
    const k = n.sentiment?.trim() ? n.sentiment : 'NOT YET READ'
    sentiment.set(k, (sentiment.get(k) ?? 0) + n.threads)
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Only narratives something has actually read. The residue bucket of unscored
            threads is always the largest and usually the fastest, and ranking it alongside
            real arguments presents a placeholder as the thing to answer. */}
        <Panel title="Arguments, by how fast they travel"
               description="A narrative is a cluster of threads making the same case. Speed, not volume, decides whether a reply arrives in time. Unread threads are excluded — they have not been characterised.">
          <RankedBars
            points={[...g.narratives]
              .filter((n) => n.threatMax != null)
              .sort((a, b) => b.velocity - a.velocity)
              .slice(0, 12)
              .map((n) => ({
                label: n.label.slice(0, 44),
                value: Number(n.velocity.toFixed(1)),
                hint: `${n.threads} threads · ${fmtNumber(n.engagements)} engagements`
                  + (n.threatMax != null ? ` · worst ${n.threatMax}/10` : '')
                  + (n.kind === 'COORDINATED' ? ' · repeated phrasing' : ''),
              }))}
            tone={(label) => {
              const n = g.narratives.find((it) => it.label.slice(0, 44) === label)
              if (!n) return undefined
              return (n.threatMax ?? 0) >= g.deployThreshold ? 'bad'
                : n.kind === 'COORDINATED' ? 'warn' : undefined
            }}
            formatValue={(n) => `${n}/min`}
            emptyMessage="No narratives in this window."
          />
        </Panel>

        <Panel title="Organic against repeated"
               description="Threads whose phrasing the astroturf scan matched, against the residue.">
          <OutcomeStrip parts={[
            { label: 'Organic spread', value: g.narratives.filter((n) => n.kind === 'ORGANIC')
                .reduce((s, n) => s + n.threads, 0), tone: 'neutral' },
            { label: 'Repeated phrasing', value: coordinated
                .reduce((s, n) => s + n.threads, 0), tone: 'warn' },
          ]} />
          <p className="mt-4 text-erp-sm text-muted-foreground">
            {coordinated.length === 0
              ? 'The scan matched no repeated phrasing in this window. That is the absence of one signal, not evidence that nothing is organised.'
              : `${coordinated.length} of ${g.narratives.length} narratives carry phrasing repeated across separate threads. This counts strings, never intent — the scan cannot and does not identify who is repeating them.`}
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <ChartFrame title="Threads by sentiment">
              <Donut
                slices={[...sentiment].map(([k, v]) => ({ id: k, label: k, value: v }))}
                total={g.totals.threads}
                centreLabel="Threads"
                formatValue={(v) => `${Math.round(v)}`}
                emptyMessage="Nothing scored in this window."
              />
            </ChartFrame>
          </div>
        </Panel>
      </div>

      <Panel
        title="Repeated phrasing, quoted"
        description="The evidence is the string itself, never a summary of it. Confidence is capped at 90 because this counts strings, not actors."
        bodyClassName="p-0"
      >
        {live.length === 0 ? (
          <p className="p-4 text-erp-sm text-muted-foreground">
            No live coordination signals. Any that were raised and stood down are kept on the
            Coordination screen with the reason.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {live.slice(0, 8).map((c) => (
              <li key={c.id} className="p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-erp-sm font-medium">“{c.phrase}”</p>
                  <span className="text-erp-xs tabular-nums text-muted-foreground">
                    confidence {c.confidence}/100
                  </span>
                </div>
                <p className="mt-1 text-erp-xs text-muted-foreground tabular-nums">
                  {c.distinctAuthors} distinct authors · {c.threadCount} threads ·{' '}
                  {c.windowMinutes} minute window · about {c.subjectName ?? 'an unnamed subject'}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  )
}

// ─── tab: reach ──────────────────────────────────────────────────────────────

function ReachTab({ g }: { g: NarrativeGraph }) {
  const mute = g.platforms.filter((p) => !p.canPost && p.threads > 0)

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Audience by platform"
               description="Engagements, not thread counts — a hundred quiet threads are a smaller problem than one loud one.">
          <Donut
            slices={g.platforms.filter((p) => p.engagements > 0)
              .map((p) => ({ id: p.code, label: p.label, value: p.engagements }))}
            total={g.totals.engagements}
            centreLabel="Engagements"
            emptyMessage="No engagements recorded in this window."
          />
        </Panel>

        <Panel title="Where we can answer, and where we cannot"
               description="A platform we can watch but not reply on is a different plan, not a smaller one.">
          <RankedBars
            points={g.platforms
              .filter((p) => p.threads > 0)
              .sort((a, b) => b.engagements - a.engagements)
              .map((p) => ({
                label: `${p.label}${p.canPost ? '' : ' — watch only'}`,
                value: p.engagements,
                hint: `${p.threads} threads · ${p.velocity.toFixed(1)}/min`
                  + (p.aboveThreshold > 0 ? ` · ${p.aboveThreshold} above threshold` : ''),
              }))}
            tone={(label) => (label.includes('watch only') ? 'warn' : undefined)}
            emptyMessage="No platform reported a thread in this window."
          />
          {mute.length > 0 && (
            <p className="mt-3 text-erp-xs text-muted-foreground">
              {fmtNumber(mute.reduce((n, p) => n + p.engagements, 0))} engagements sit on{' '}
              {mute.map((p) => p.label).join(', ')}, where no reply API exists. Drafts for these
              will sit in the queue with nowhere to go — the answer has to leave social.
            </p>
          )}
        </Panel>
      </div>

      <Panel title="Subjects, by what is said about them"
             description="Hostile share is a reading of tone; engagements is a count of reach. They are shown together because either alone misleads.">
        <div className="space-y-3">
          {g.subjects.length === 0 ? (
            <p className="text-erp-sm text-muted-foreground">No subject has a thread in this window.</p>
          ) : g.subjects.map((s) => (
            <div key={s.id} className="grid grid-cols-[minmax(120px,22%)_1fr_auto] items-center gap-3">
              <span className="truncate text-erp-sm" title={s.name ?? undefined}>
                {s.name ?? 'Unnamed subject'}
              </span>
              <span className="h-2 rounded bg-muted overflow-hidden" role="img"
                    aria-label={`${Math.round(s.hostileShare * 100)}% hostile`}>
                <span className="block h-full bg-rose-500/70"
                      style={{ width: `${Math.min(100, s.hostileShare * 100)}%` }} />
              </span>
              <span className="tabular-nums text-erp-xs text-muted-foreground whitespace-nowrap">
                {Math.round(s.hostileShare * 100)}% hostile · {fmtNumber(s.engagements)} eng ·{' '}
                {s.threads} threads
                {s.threatMean != null && ` · mean ${s.threatMean.toFixed(1)}/10`}
              </span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  )
}
